// Copyright 2026 AllSpice. All rights reserved.

type SearchTargetLike = {
  category: string;
  dataSelector: string;
  description?: string;
  normalizedTitle?: string;
  pageId?: string | null;
  sourceSvg?: SVGSVGElement | null;
  title: string;
};

type SearchTargetGroup<T extends SearchTargetLike = SearchTargetLike> = {
  key: string;
  category: string;
  description: string;
  targets: T[];
  title: string;
};

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase();
}

function groupedTargetKey(target: SearchTargetLike): string {
  return `${target.category}::${normalizeSearchValue(target.title)}`;
}

function groupedTargetDescription<T extends SearchTargetLike>(targets: T[]): string {
  const firstTarget = targets[0];
  if (!firstTarget) {
    return '';
  }

  if (targets.length === 1) {
    return firstTarget.description ?? '';
  }

  return `${targets.length} instances`;
}

function mergeTargetFields<T extends SearchTargetLike>(existing: T, incoming: T): void {
  if (!existing.description && incoming.description) {
    existing.description = incoming.description;
  }
  if (!existing.pageId && incoming.pageId) {
    existing.pageId = incoming.pageId;
  }
  if (!existing.sourceSvg && incoming.sourceSvg) {
    existing.sourceSvg = incoming.sourceSvg;
  }
}

function searchTargetBaseKey(target: SearchTargetLike): string {
  return `${groupedTargetKey(target)}\0${target.dataSelector}`;
}

function searchTargetExactKey(baseKey: string, pageId: string | null | undefined): string {
  return `${baseKey}\0${pageId ?? ''}`;
}

/**
 * Map-based index for target deduplication. Owns the deduplicated target
 * array. A target with no pageId matches any target sharing the same
 * category, title, and dataSelector.
 */
export class SearchTargetIndex<T extends SearchTargetLike> {
  private readonly exactIndex = new Map<string, T>();
  private readonly baseIndex = new Map<string, T>();
  readonly targets: T[] = [];

  addOrMerge(incoming: T): void {
    incoming.normalizedTitle = normalizeSearchValue(incoming.title);

    const bk = searchTargetBaseKey(incoming);
    const ek = searchTargetExactKey(bk, incoming.pageId);

    const exact = this.exactIndex.get(ek);
    if (exact) {
      mergeTargetFields(exact, incoming);
      return;
    }

    if (incoming.pageId) {
      // Incoming has a pageId — check for existing wildcard entry (no pageId).
      const wildcardEk = searchTargetExactKey(bk, null);
      const wildcard = this.exactIndex.get(wildcardEk);
      if (wildcard) {
        mergeTargetFields(wildcard, incoming);
        this.exactIndex.delete(wildcardEk);
        this.exactIndex.set(ek, wildcard);
        return;
      }
    } else {
      // Incoming has no pageId — matches any existing entry with same base key.
      const base = this.baseIndex.get(bk);
      if (base) {
        mergeTargetFields(base, incoming);
        return;
      }
    }

    this.exactIndex.set(ek, incoming);
    if (!this.baseIndex.has(bk)) {
      this.baseIndex.set(bk, incoming);
    }
    this.targets.push(incoming);
  }
}

export function groupSearchTargets<T extends SearchTargetLike>(targets: T[]): SearchTargetGroup<T>[] {
  const groupedTargets = new Map<string, SearchTargetGroup<T>>();
  const orderedGroups: SearchTargetGroup<T>[] = [];

  for (const target of targets) {
    const key = groupedTargetKey(target);
    let group = groupedTargets.get(key);
    if (!group) {
      group = {
        key,
        category: target.category,
        description: '',
        targets: [],
        title: target.title,
      };
      groupedTargets.set(key, group);
      orderedGroups.push(group);
    }

    group.targets.push(target);
  }

  for (const group of orderedGroups) {
    group.description = groupedTargetDescription(group.targets);
  }

  return orderedGroups;
}

function filterByTitleMatch<T>(
  items: T[],
  getNormalizedTitle: (item: T) => string,
  query: string,
  limit: number,
): T[] {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) {
    return items.slice(0, limit);
  }

  // Single pass: bucket items by match quality.
  const exact: T[] = [];
  const prefix: T[] = [];
  const contains: T[] = [];

  for (const item of items) {
    const normalizedTitle = getNormalizedTitle(item);
    if (normalizedTitle === normalizedQuery) {
      exact.push(item);
    } else if (normalizedTitle.startsWith(normalizedQuery)) {
      prefix.push(item);
    } else if (normalizedTitle.includes(normalizedQuery)) {
      contains.push(item);
    }
  }

  if (exact.length > 0 || prefix.length > 0) {
    return [...exact, ...prefix].slice(0, limit);
  }

  return contains.slice(0, limit);
}

function normalizedTitleOf<T extends SearchTargetLike>(target: T): string {
  return target.normalizedTitle ?? normalizeSearchValue(target.title);
}

export function filterSearchTargets<T extends SearchTargetLike>(targets: T[], query: string, limit = 50): T[] {
  return filterByTitleMatch(targets, normalizedTitleOf, query, limit);
}

export function filterGroupedSearchTargets<T extends SearchTargetLike>(
  targets: T[],
  query: string,
  limit = 50,
): SearchTargetGroup<T>[] {
  const groupedTargets = groupSearchTargets(targets);
  return filterByTitleMatch(groupedTargets, (g) => normalizeSearchValue(g.title), query, limit);
}

/** Filter pre-grouped targets — avoids re-grouping on every query change. */
export function filterGroups<T extends SearchTargetLike>(
  groups: SearchTargetGroup<T>[],
  query: string,
  limit = 50,
): SearchTargetGroup<T>[] {
  return filterByTitleMatch(groups, (g) => normalizeSearchValue(g.title), query, limit);
}

export type {SearchTargetGroup, SearchTargetLike};
